import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      // Only force-logout on actual auth failures (401/403). Network blips,
      // 500s, etc. should leave the existing session alone — otherwise a
      // single transient hiccup during a long upload kicks the user back to /login.
      const status = error.response?.status;
      if (status === 401 || status === 403) {
        console.warn('Auth token rejected by /auth/me — logging out');
        logout();
      } else {
        console.error('Transient /auth/me failure (keeping session):', status || error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, referralCode) => {
    const payload = { email, password, name };
    if (referralCode) payload.referral_code_used = referralCode;
    const response = await axios.post(`${API_URL}/api/auth/register`, payload);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  // Used by /register to finish the registration AFTER recording legal acceptance,
  // so LegalAcceptanceModal doesn't race-fire on /memberships.
  const setAuth = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getAuthHeader, refreshUser: fetchUser, setAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
